import { sentenceCase } from 'change-case';
import { useCallback, useEffect, useState } from 'react';
// material
import {
  Card,
  Checkbox,
  Container,
  Stack,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableRow
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useDispatch } from 'react-redux';
import { Box } from '@mui/system';
// components
import BrudCrumbs from '../../components/BreadCrumbs';
import Page from '../../components/Page';
import PageTitle from '../../components/PageHeading';

import Label from '../../components/Label';
import Scrollbar from '../../components/Scrollbar';
import SearchNotFound from '../../components/SearchNotFound';
import { UserListHead, UserMoreMenu } from '../../components/_dashboard/user';
//
import { fDate, fDateTime2 } from '../../utils/formatTime';
import { NOT_AVAILABLE } from '../Global/Constants';
import LogInToolbar from './LogInToolbar';
import useLoginModuleSelector from '../../store/Slice/LoginModule/LoginModuleSelector';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import { getLoginGridData } from '../../store/Slice/LoginModule/LoginModuleSlice';
import SimplePagination from '../../components/SimplePagination';
import useDebounce from '../../utils/useDebounce';

// ----------------------------------------------------------------------

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover
  },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0
  }
}));
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    fontSize: 13,
    fontWeight: 'bold',
    padding: '6px'
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 11,
    padding: '4px 6px'
  }
}));

const TABLE_HEAD = [
  { id: 'id', label: 'APPLICATION ID', alignRight: false },
  { id: 'created', label: 'Submission Date', alignRight: false },
  { id: 'name', label: 'NAME', alignRight: false },
  { id: 'loanType', label: 'Product', alignRight: false },
  { id: 'city', label: 'City', alignRight: false },
  { id: 'amount', label: 'Amount', alignRight: false },
  { id: 'bankName', label: 'Bank', alignRight: false },
  { id: 'status', label: 'status', alignRight: false },
  { id: 'created', label: 'Last update', alignRight: false },
  { id: 'action', label: 'ACTION' }
];

// ----------------------------------------------------------------------

/**
 * Represents a book.
 * @constructor
 * @param {string} title - The title of the book.
 * @param {string} author - The author of the book.
 */
export default function LogIn() {
  const [order, setOrder] = useState('desc');
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState('name');
  const [filterName, setFilterName] = useState('');
  const [filterState, setFilterState] = useState({
    product: ' ',
    productId: null,
    productArray: [],
    city: null,
    cityId: null,
    cityArray: [],
    status: ' ',
    statusId: null,
    statusArray: [],
    employmentType: ' ',
    employmentTypeId: null,
    employmentTypeArray: []
  });
  const [filterDatesRange, setFilterDatesRange] = useState([
    {
      startDate: null,
      endDate: null,
      key: 'selection'
    }
  ]);
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(100);
  const [hasNextPage, setHasNextPage] = useState(true);

  const dispatch = useDispatch();

  const { LoginData } = useLoginSelector();
  const { LoginGridData } = useLoginModuleSelector();
  const debouncedSearch = useDebounce(filterName, 600);

  const getLoginGridApi = useCallback(() => {
    const objPayload = {
      companyId: LoginData?.companyId,
      userId: LoginData?.userId,
      accessLevelId: LoginData?.accessLevelId,
      stageId: '58',
      log_inFilterModel: {
        cityId: filterState.cityId || null,
        fromLeadDate:
          filterDatesRange[0].startDate !== null ? fDateTime2(filterDatesRange[0].startDate) : null,
        toLeadDate:
          filterDatesRange[0].endDate !== null ? fDateTime2(filterDatesRange[0].endDate) : null,
        leadStatusId: filterState.status || null,
        loanTypeId: filterState.product || null
      },
      entityListRequestModel: {
        searchLiteral: debouncedSearch?.length >= 2 ? debouncedSearch : null,
        sortOrder: order || null,
        sortLiteral: order || null,
        pageSize: rowsPerPage,
        pageNumber: page + 1
      }
    };
    dispatch(getLoginGridData(objPayload));
  }, [
    LoginData,
    debouncedSearch,
    filterDatesRange,
    filterState,
    order,
    page,
    rowsPerPage,
    dispatch
  ]);

  useEffect(() => {
    getLoginGridApi();
  }, [getLoginGridApi]);

  useEffect(() => {
    const leadDetails = localStorage.getItem('leadDetails');
    if (leadDetails !== null) {
      localStorage.removeItem('leadDetails');
    }
  }, []);

  const handleRequestSort = (event, property) => {
    if (property !== 'action') {
      setOrder(orderBy === property && order === 'asc' ? 'desc' : 'asc');
      setOrderBy(property);
    }
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = LoginGridData?.map((n) => n);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
  };

  const handleClick = (event, name) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
  };

  useEffect(() => {
    if (!LoginGridData) {
      setHasNextPage(true);
      return;
    }

    // If API returned fewer rows → last page
    setHasNextPage(LoginGridData.length >= rowsPerPage);
  }, [LoginGridData, rowsPerPage]);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch]);

  return (
    <Page title="Kpros by Kalolytic Solutions || Log In">
      <Container maxWidth="xl">
        <Box pt={3} pb={3} mb={4}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            className="pageHeading"
          >
            <PageTitle info="List of Application" />
            <Box>
              <BrudCrumbs />
            </Box>
          </Stack>
          <Card>
            <LogInToolbar
              numSelected={selected.length}
              filterName={filterName}
              setFilterName={setFilterName}
              onFilterName={(e) => setFilterName(e.target.value)}
              // filterDatesRange={filterDatesRange}
              filterState={filterState}
              setFilterState={setFilterState}
              selected={selected}
              parent="login"
            />
            <Scrollbar>
              <TableContainer sx={{ minWidth: 800, minHeight: '60vh', overflow: 'auto' }}>
                <Table>
                  <UserListHead
                    order={order}
                    orderBy={orderBy}
                    headLabel={TABLE_HEAD}
                    rowCount={LoginGridData?.length}
                    numSelected={selected.length}
                    onRequestSort={handleRequestSort}
                    onSelectAllClick={handleSelectAllClick}
                    isCheckBox="true"
                  />
                  {/* {console.log('LoginGridData222', LoginGridData)} */}
                  <TableBody>
                    {LoginGridData?.map((item, index) => {
                      const isItemSelected = selected.indexOf(item) !== -1;
                      return (
                        <StyledTableRow
                          hover
                          key={item.applicationId}
                          tabIndex={-1}
                          role="checkbox"
                          selected={isItemSelected}
                          aria-checked={isItemSelected}
                          parent="login"
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={isItemSelected}
                              onChange={(event) => handleClick(event, item)}
                            />
                          </TableCell>
                          <StyledTableCell align="left">
                            {item?.applicationId !== null && item?.applicationId !== ''
                              ? item?.applicationId
                              : NOT_AVAILABLE}
                          </StyledTableCell>
                          <StyledTableCell align="left">
                            {' '}
                            {item?.submissionDate !== null && item?.submissionDate !== ''
                              ? fDate(item.submissionDate)
                              : NOT_AVAILABLE}
                          </StyledTableCell>

                          <StyledTableCell align="left">
                            {item?.leadName !== null && item?.leadName !== ''
                              ? item?.leadName
                              : NOT_AVAILABLE}
                          </StyledTableCell>
                          <StyledTableCell align="left">
                            {item?.loanTypeName !== null && item?.loanTypeName !== ''
                              ? item?.loanTypeName
                              : NOT_AVAILABLE}
                          </StyledTableCell>
                          <StyledTableCell align="left">
                            {item?.cityName !== null && item?.cityName !== ''
                              ? item?.cityName
                              : NOT_AVAILABLE}
                          </StyledTableCell>
                          <StyledTableCell align="left">
                            {item?.loanAmount !== null && item?.loanAmount !== ''
                              ? item?.loanAmount
                              : NOT_AVAILABLE}
                          </StyledTableCell>
                          <StyledTableCell align="left">
                            {item?.bankPrefName !== null && item?.bankPrefName !== ''
                              ? item?.bankPrefName
                              : NOT_AVAILABLE}
                          </StyledTableCell>
                          <StyledTableCell align="left">
                            <Label
                              variant="ghost"
                              color={
                                item?.logInStatusName === 'Closed' ||
                                item?.logInStatusName === 'Rejected'
                                  ? 'warning'
                                  : 'success'
                              }
                            >
                              {item?.logInStatusName !== null && item?.logInStatusName !== ''
                                ? sentenceCase(item?.logInStatusName)
                                : NOT_AVAILABLE}
                            </Label>
                          </StyledTableCell>
                          <StyledTableCell align="left">
                            {item?.lastUpdatedOn !== null && item?.lastUpdatedOn !== ''
                              ? fDate(item.lastUpdatedOn)
                              : NOT_AVAILABLE}
                          </StyledTableCell>

                          <StyledTableCell align="">
                            <UserMoreMenu rowId={item} parent="login" />
                          </StyledTableCell>
                        </StyledTableRow>
                      );
                    })}
                  </TableBody>
                  {LoginGridData?.length === 0 && (
                    <TableBody>
                      <TableRow>
                        <StyledTableCell align="center" colSpan={12} sx={{ py: 3 }}>
                          <SearchNotFound searchQuery={filterName} />
                        </StyledTableCell>
                      </TableRow>
                    </TableBody>
                  )}
                </Table>
              </TableContainer>
            </Scrollbar>
            <SimplePagination page={page} setPage={setPage} hasNextPage={hasNextPage} />
          </Card>
        </Box>
      </Container>
    </Page>
  );
}
